import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Modal, Button } from 'react-bootstrap';
import { ShareFill } from 'react-bootstrap-icons';
import { useSelector } from "react-redux";
import config from '../config';
import Header from '../components/Header';
import '../styles/covidtestresult.css';
import { getCovidTestImagesById } from '../api';
import CovidResultGradient from '../components/CovidResultGradient';
import CovidResultPDFV2 from '../components/CovidResultPDFV2';

export default function CovidTestResultV2(props) {
    const result = props.location.state?.result;
    const user = useSelector( state => state.user);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [open, setOpen] = useState(false);
    const [isProcess, setProcess] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isReady, setReady] = useState(false);
    const [pdfData, setPdfData] = useState(null);

    const getArrowPos = async () => {
        if(result.threshold) {
            const probability = parseFloat(result.probability.toFixed(2));
            if(probability < result.threshold) {
                let high = parseFloat((result.threshold - 0.01).toFixed(2));
                let low = parseFloat((high - 0.09).toFixed(2));
                let pos = 35;
                for(let i=0; i<5; i++) {
                    if(probability >= low && probability <= high) {
                        return pos
                    }
                    high = parseFloat((low - 0.01).toFixed(2));
                    low = parseFloat((high - 0.09).toFixed(2));
                    pos -= 5;
                }
                return pos;
            } else {
                let low = result.threshold;
                let high = parseFloat((low + 0.09).toFixed(2));
                let pos = 65;
                for(let i=0; i<5; i++) {
                    if(probability >= low && probability <= high) {
                        return pos
                    }
                    low = parseFloat((high + 0.01).toFixed(2));
                    high = parseFloat((low + 0.09).toFixed(2));
                    pos += 5;
                }
                return pos;
            }
        } else {
            return result.isCovid ? 75 : 25;
        }
    }

    const getPdfData = async (img) => {
        const questionnaire = result.questionnaire;
        const isASymp = questionnaire ? questionnaire?.cough === "False" &&  questionnaire?.headache === "False" &&  questionnaire?.weakness === "False" &&  questionnaire?.sore_throat === "False" : true;
        const pos = await getArrowPos();
        
        let pictures = [];
        if(!result.isDemo) {
            const res = await getCovidTestImagesById(result.id);
            pictures = res.pictures;
        }        

        const canvasElement = document.createElement('canvas');
        canvasElement.width = img.width
        canvasElement.height = img.height
        const context = canvasElement.getContext('2d');
        context.drawImage(img, 0, 0);
        const qrcodeData = canvasElement.toDataURL();
        setReady(true)
        setPdfData({
            pictures,
            svgData: qrcodeData,
            name: `${user.firstName} ${user.lastName}`,
            date: `${result.date} ${result.time}`,
            errMsg: "",
            isASymp,
            pos
        })
    }

    const onDownloadPDF = () => {
        setOpen(true)
    };

    const handleClose = () => {
        setOpen(false)
    }

    useEffect(() => {
        if(isProcess) {
            setLoading(true);
            setOpen(false);
            if(pdfUrl) {
                const currentTimestamp = new Date().getTime();
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `COVID-Result-${currentTimestamp}.pdf`;
                link.click();
                setProcess(false)
                setLoading(false);
            }
        }        
    }, [pdfUrl, isProcess]);

    useEffect(() => {
        const qrCode = document.getElementById('result_qrcode');
        const serializedSVG = new XMLSerializer().serializeToString(qrCode);

        const img = new Image();
        img.onload = () => {
          getPdfData(img)
        };
        img.src = `data:image/svg+xml;base64,${btoa(serializedSVG)}`;
    }, [])

    return (
        <div className='container'>
            <div className='main covidtestresult' data-testid="covidtestresult">
                <Header/>
                {isLoading && <div id="cover-spin"></div>}
                <p className='mt-3 font-weight-bold'>{user.firstName} {user.lastName}</p>                

                <div className='result-box'>
                    <div className='share_btn'>
                        {
                            isReady && pdfData !== null && <PDFDownloadLink document={<CovidResultPDFV2 data={pdfData} />} fileName="covid_result.pdf">
                                {({ blob, url, loading, error }) => setPdfUrl(url)}
                            </PDFDownloadLink>
                        }
                        <div data-testid="share_icon" className='share_icon rounded-circle' onClick={(e) => onDownloadPDF()}><ShareFill size={20} color='#007bff' /></div>
                    </div>

                    <h5 className='font-weight-bold mb-2'><span>COVID-19</span></h5>

                    <div className=''>
                        <div className='' data-testid="qr-code">
                            <div className='bg-white'>
                                <QRCode id='result_qrcode' value={config.QRcode_URL + '/covid/' + result?.id} size={100} renderAs={"svg"}/>
                            </div>
                            <p className='mt-3'><span>Date: </span> <span className='font-weight-bold'>{result.date}</span> <br/>
                            <span>Time: </span><span className='font-weight-bold'>{result.time}</span></p>
                        </div>
                    </div>
                    
                    <CovidResultGradient 
                        threshold={result.threshold}
                        probability={result.probability}
                        isCovid={result.isCovid}
                        questionnaire={result.questionnaire}
                    >
                    </CovidResultGradient>
                </div>
            </div>

            <Modal animation={false} show={open} onHide={handleClose} centered backdrop="static" className='warning_modal' data-testid="warning_modal">
                <Modal.Header>
                    <Modal.Title>COVID-19 Screening Result Sharing</Modal.Title>
                </Modal.Header>
                <Modal.Body className="body">
                    <p>I understand that by sharing this information I am disclosing personal health information to the recipient and that the information shared is no longer protected by the iSelfie application privacy and security controls.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' id='cancel' data-testid="cancel" onClick={handleClose}>Cancel</Button>
                    <Button variant='primary' id='continue' data-testid="continue" onClick={() => setProcess(true)}>Share</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
